<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-account-heart-outline me-2"></i>{{ $t('page.chu_chp', 'Community Health (CHU / CHP)') }}</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.chu_count || 0 }}</div><div class="hms-stat-lbl">CHUs</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.chp_count || 0 }}</div><div class="hms-stat-lbl">Active CHPs</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ formatNumber(stats.households_assigned || 0) }}</div><div class="hms-stat-lbl">HH Assigned</div></div>
        <div class="hms-stat"><div class="hms-stat-num">{{ formatNumber(stats.households_registered || 0) }}</div><div class="hms-stat-lbl">HH Registered</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.referrals_30d || 0 }}</div><div class="hms-stat-lbl">Referrals (30d)</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='units' }" @click="tab='units'"><i class="mdi mdi-home-group me-1"></i>CHUs</button>
        <button class="hms-tab" :class="{ active: tab==='chps' }" @click="tab='chps'; loadChps()"><i class="mdi mdi-account-heart me-1"></i>CHPs</button>
        <button class="hms-tab" :class="{ active: tab==='households' }" @click="tab='households'; loadHouseholds()"><i class="mdi mdi-home me-1"></i>Households</button>
      </div>

      <!-- ============ CHU TAB ============ -->
      <div v-if="tab==='units'">
        <div class="d-flex justify-content-end mb-2">
          <button class="hms-btn hms-btn-success" @click="openChuModal()"><i class="mdi mdi-plus"></i> New CHU</button>
        </div>
        <div class="hms-grid">
          <div v-for="cu in units" :key="cu.chu_id" class="hms-chu-card">
            <div class="hms-chu-head">
              <div>
                <div class="hms-name">{{ cu.chu_name }}</div>
                <div class="hms-muted-sm">{{ cu.chu_code }} · Link: {{ cu.link_facility || '—' }}</div>
              </div>
              <span class="hms-pill hms-pill-info">{{ cu.chps_count || 0 }} CHPs</span>
            </div>
            <div class="hms-chu-meta">
              <div><span class="hms-vk">Sub-county</span> {{ cu.sub_county || '—' }}</div>
              <div><span class="hms-vk">Ward</span> {{ cu.ward || '—' }}</div>
              <div><span class="hms-vk">HH</span> {{ formatNumber(cu.households_count || 0) }}</div>
              <div><span class="hms-vk">Pop</span> {{ formatNumber(cu.population || 0) }}</div>
            </div>
            <div v-if="cu.chew_name" class="hms-muted-sm">CHEW: {{ cu.chew_name }} <span v-if="cu.chew_phone">· {{ cu.chew_phone }}</span></div>
            <div class="hms-chu-actions">
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="filterChpsByChu(cu)"><i class="mdi mdi-account-heart"></i> CHPs ({{ cu.chps_count }})</button>
              <button class="hms-btn-link" @click="openChuModal(cu)"><i class="mdi mdi-pencil"></i></button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ CHPs TAB ============ -->
      <div v-if="tab==='chps'">
        <div class="d-flex justify-content-between mb-2">
          <select v-model="chpFilterChu" class="hms-input" style="max-width:280px" @change="loadChps">
            <option value="">All CHUs</option>
            <option v-for="cu in units" :key="cu.chu_id" :value="cu.chu_id">{{ cu.chu_name }}</option>
          </select>
          <button class="hms-btn hms-btn-success" @click="openChpModal()"><i class="mdi mdi-account-plus"></i> Enrol CHP</button>
        </div>
        <table class="hms-table">
          <thead>
            <tr>
              <th>CHP #</th><th>Name</th><th>Sex</th><th>Phone</th><th>CHU</th>
              <th>HH Assigned</th><th>HH Reg.</th><th>Visits 30d</th><th>Kit / Phone</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in chps" :key="c.chp_id">
              <td><strong>{{ c.chp_number }}</strong></td>
              <td>{{ c.first_name }} {{ c.last_name }}<div class="hms-muted-sm">{{ c.village || '' }}</div></td>
              <td>{{ c.sex || '—' }}</td>
              <td>{{ c.phone || '—' }}</td>
              <td>{{ c.chu_name }}</td>
              <td>{{ c.households_assigned }}</td>
              <td>{{ c.hh_registered }}</td>
              <td>{{ c.visits_30d }}</td>
              <td>
                <span v-if="c.kit_issued == 1" class="hms-tag hms-tag-info">Kit</span>
                <span v-if="c.phone_issued == 1" class="hms-tag hms-tag-success">eCHIS</span>
              </td>
              <td>
                <button class="hms-btn-link" @click="openChpModal(c)"><i class="mdi mdi-pencil"></i></button>
                <button class="hms-btn hms-btn-blue hms-btn-sm" @click="filterHouseholdsByChp(c)"><i class="mdi mdi-home"></i></button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ HOUSEHOLDS TAB ============ -->
      <div v-if="tab==='households'">
        <div class="d-flex justify-content-between mb-2">
          <div class="d-flex gap-2">
            <input v-model="hhSearch" class="hms-input" placeholder="Search head name / HH no / phone" @keyup.enter="loadHouseholds" />
            <select v-model="hhFilterChp" class="hms-input" @change="loadHouseholds" style="max-width:280px">
              <option value="">All CHPs</option>
              <option v-for="c in chps" :key="c.chp_id" :value="c.chp_id">{{ c.first_name }} {{ c.last_name }} ({{ c.chp_number }})</option>
            </select>
          </div>
          <button class="hms-btn hms-btn-success" @click="openHhModal()"><i class="mdi mdi-home-plus"></i> Register Household</button>
        </div>
        <table class="hms-table">
          <thead>
            <tr>
              <th>HH #</th><th>Head</th><th>Village</th><th>CHU / CHP</th>
              <th>Members</th><th>U5</th><th>WCBA</th><th>Preg</th><th>SHA</th><th>WASH</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="h in households" :key="h.household_id">
              <td><strong>{{ h.household_no }}</strong></td>
              <td>{{ h.head_name }}<div class="hms-muted-sm">{{ h.head_phone || '' }}</div></td>
              <td>{{ h.village || '—' }}</td>
              <td>{{ h.chu_name }}<div class="hms-muted-sm">{{ h.chp_name || '—' }}</div></td>
              <td>{{ h.members_total }}</td>
              <td>{{ h.under_5 }}</td>
              <td>{{ h.wcba }}</td>
              <td>{{ h.pregnant_count }}</td>
              <td>
                <span v-if="h.nhif_sha_enrolled == 1" class="hms-tag hms-tag-success">SHA</span>
                <span v-else class="hms-tag hms-tag-warn">No</span>
              </td>
              <td>
                <span v-if="h.has_latrine == 1" title="Latrine"><i class="mdi mdi-water-outline"></i></span>
                <span v-if="h.safe_water_source == 1" title="Safe water"><i class="mdi mdi-water-check"></i></span>
                <span v-if="h.has_llin == 1" title="LLIN"><i class="mdi mdi-shield-bug"></i></span>
              </td>
              <td>
                <button class="hms-btn-link" @click="openHhModal(h)"><i class="mdi mdi-pencil"></i></button>
                <button class="hms-btn hms-btn-success hms-btn-sm" @click="openVisitModal(h)"><i class="mdi mdi-clipboard-plus"></i></button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- =============== CHU MODAL =============== -->
    <div v-if="chuModal" class="hms-modal-bg" @click.self="chuModal=false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head"><h5>{{ chu.chu_id ? 'Edit CHU' : 'New CHU' }}</h5><button class="hms-icon-btn" @click="chuModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">CHU code *</label><input v-model="chu.chu_code" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Link facility *</label>
              <select v-model.number="chu.branch_id" class="hms-input">
                <option v-for="b in branches" :key="b.branch_id" :value="b.branch_id">{{ b.branch_name }}</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">CHU name *</label><input v-model="chu.chu_name" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Sub-county</label><input v-model="chu.sub_county" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Ward</label><input v-model="chu.ward" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Village</label><input v-model="chu.village" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">HH count</label><input v-model.number="chu.households_count" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Population</label><input v-model.number="chu.population" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Latitude</label><input v-model.number="chu.latitude" type="number" step="0.00000001" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Longitude</label><input v-model.number="chu.longitude" type="number" step="0.00000001" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">CHEW name</label><input v-model="chu.chew_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">CHEW phone</label><input v-model="chu.chew_phone" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="chuModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveChu">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== CHP MODAL =============== -->
    <div v-if="chpModal" class="hms-modal-bg" @click.self="chpModal=false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head"><h5>{{ chp.chp_id ? 'Edit CHP' : 'Enrol CHP' }}</h5><button class="hms-icon-btn" @click="chpModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">CHP # *</label><input v-model="chp.chp_number" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">National ID</label><input v-model="chp.national_id" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">First name *</label><input v-model="chp.first_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Last name *</label><input v-model="chp.last_name" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Sex</label>
              <select v-model="chp.sex" class="hms-input"><option value="">—</option><option value="M">M</option><option value="F">F</option></select>
            </div>
            <div class="col-5"><label class="hms-label">Phone</label><input v-model="chp.phone" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Enrolled date</label><input v-model="chp.enrolled_date" type="date" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">CHU *</label>
              <select v-model.number="chp.chu_id" class="hms-input">
                <option v-for="cu in units" :key="cu.chu_id" :value="cu.chu_id">{{ cu.chu_name }} (link: {{ cu.link_facility || '—' }})</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Village</label><input v-model="chp.village" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">HH assigned</label><input v-model.number="chp.households_assigned" type="number" class="hms-input" /></div>
            <div class="col-4 d-flex align-items-end"><label><input type="checkbox" v-model="chp.kit_issued" :true-value="1" :false-value="0" /> Kit issued</label></div>
            <div class="col-4 d-flex align-items-end"><label><input type="checkbox" v-model="chp.phone_issued" :true-value="1" :false-value="0" /> eCHIS phone</label></div>
            <div class="col-4"><label class="hms-label">Stipend a/c</label><input v-model="chp.stipend_account" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="chpModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveChp">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== HOUSEHOLD MODAL =============== -->
    <div v-if="hhModal" class="hms-modal-bg" @click.self="hhModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>{{ hh.household_id ? 'Edit Household' : 'Register Household' }}</h5><button class="hms-icon-btn" @click="hhModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Head of household *</label><input v-model="hh.head_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Head phone</label><input v-model="hh.head_phone" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">CHU *</label>
              <select v-model.number="hh.chu_id" class="hms-input" @change="onChuChange">
                <option v-for="cu in units" :key="cu.chu_id" :value="cu.chu_id">{{ cu.chu_name }}</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">CHP</label>
              <select v-model.number="hh.chp_id" class="hms-input">
                <option :value="null">— unassigned —</option>
                <option v-for="c in chpsForHh" :key="c.chp_id" :value="c.chp_id">{{ c.first_name }} {{ c.last_name }} ({{ c.chp_number }})</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Village</label><input v-model="hh.village" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">GPS Lat</label><input v-model.number="hh.gps_lat" type="number" step="0.00000001" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">GPS Lon</label><input v-model.number="hh.gps_lon" type="number" step="0.00000001" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Members</label><input v-model.number="hh.members_total" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Under 5</label><input v-model.number="hh.under_5" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">WCBA</label><input v-model.number="hh.wcba" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Pregnant</label><input v-model.number="hh.pregnant_count" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Disabled</label><input v-model.number="hh.disabled_count" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">≥65</label><input v-model.number="hh.elderly_count" type="number" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="hh.has_latrine" :true-value="1" :false-value="0" /> Latrine</label></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="hh.safe_water_source" :true-value="1" :false-value="0" /> Safe water</label></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="hh.has_llin" :true-value="1" :false-value="0" /> LLIN</label></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="hh.nhif_sha_enrolled" :true-value="1" :false-value="0" /> SHA enrolled</label></div>
            <div class="col-12"><label class="hms-label">Risk factors</label><input v-model="hh.risk_factors" class="hms-input" placeholder="e.g. TB contact, malnutrition, child not immunized" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="hhModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveHh">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== VISIT MODAL =============== -->
    <div v-if="visitModal" class="hms-modal-bg" @click.self="visitModal=false">
      <div class="hms-modal" style="width:540px">
        <div class="hms-modal-head"><h5>Household Visit — {{ visit._hh_name }}</h5><button class="hms-icon-btn" @click="visitModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Date *</label><input v-model="visit.visit_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">CHP *</label>
              <select v-model.number="visit.chp_id" class="hms-input">
                <option v-for="c in chps" :key="c.chp_id" :value="c.chp_id">{{ c.first_name }} {{ c.last_name }} ({{ c.chp_number }})</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Purpose</label>
              <select v-model="visit.visit_purpose" class="hms-input">
                <option value="routine">Routine</option>
                <option value="sick_member">Sick member</option>
                <option value="pregnant_followup">Pregnant follow-up</option>
                <option value="child_followup">Child follow-up</option>
                <option value="health_education">Health education</option>
                <option value="default_tracing">Default tracing</option>
                <option value="immunization_followup">Immunization follow-up</option>
                <option value="referral">Referral</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Findings</label><textarea v-model="visit.findings" class="hms-input" rows="2"></textarea></div>
            <div class="col-6"><label class="hms-label">Referrals made</label><input v-model.number="visit.referrals_made" type="number" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Referred to facility</label>
              <select v-model.number="visit.referred_to_branch_id" class="hms-input">
                <option :value="null">—</option>
                <option v-for="b in branches" :key="b.branch_id" :value="b.branch_id">{{ b.branch_name }}</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Commodities distributed</label><input v-model="visit.commodities_distributed" class="hms-input" placeholder="ORS, zinc, FP, LLIN…" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="visit.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="visitModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveVisit">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "CommunityHealth",
  components: { Navigation },
  data() {
    return {
      tab: 'units', saving: false,
      stats: {}, units: [], chps: [], households: [], branches: [],

      chpFilterChu: '', hhFilterChp: '', hhSearch: '',

      chuModal: false, chu: this.blankChu(),
      chpModal: false, chp: this.blankChp(),
      hhModal: false,  hh:  this.blankHh(),
      visitModal: false, visit: this.blankVisit()
    };
  },
  computed: {
    chpsForHh() {
      if (!this.hh.chu_id) return this.chps;
      return this.chps.filter(c => c.chu_id == this.hh.chu_id);
    }
  },
  mounted() { this.loadAll(); },
  methods: {
    blankChu() { return { chu_id: null, branch_id: null, chu_code: '', chu_name: '', sub_county: '', ward: '', village: '', households_count: null, population: null, chew_name: '', chew_phone: '', latitude: null, longitude: null }; },
    blankChp() { return { chp_id: null, chu_id: null, branch_id: null, chp_number: '', national_id: '', first_name: '', last_name: '', sex: '', phone: '', village: '', households_assigned: 100, enrolled_date: new Date().toISOString().substring(0,10), kit_issued: 1, phone_issued: 0, stipend_account: '' }; },
    blankHh()  { return { household_id: null, branch_id: null, chu_id: null, chp_id: null, head_name: '', head_phone: '', village: '', gps_lat: null, gps_lon: null, members_total: 0, under_5: 0, wcba: 0, pregnant_count: 0, disabled_count: 0, elderly_count: 0, has_latrine: 0, safe_water_source: 0, has_llin: 0, nhif_sha_enrolled: 0, risk_factors: '' }; },
    blankVisit() { return { household_id: null, chp_id: null, _hh_name: '', visit_date: new Date().toISOString().substring(0,10), visit_purpose: 'routine', findings: '', referrals_made: 0, referred_to_branch_id: null, commodities_distributed: '', notes: '' }; },

    async loadAll() {
      try {
        const [u, b] = await Promise.all([
          executeJson({ function: 'getCommunityUnits' }, hms),
          executeJson({ function: 'getBranches' }, hms)
        ]);
        if (u.data && u.data.success) {
          const d = u.data.data || {};
          this.units = d.Units || [];
          this.stats = d.Stats || {};
        }
        if (b.data && b.data.success) {
          this.branches = (b.data.data && b.data.data.Branches) || [];
        }
      } catch (e) { console.error(e); }
    },
    async loadChps() {
      try {
        const r = await executeJson({ function: 'getCommunityChps', chu_id: this.chpFilterChu }, hms);
        if (r.data && r.data.success) this.chps = (r.data.data && r.data.data.Chps) || [];
      } catch (e) { console.error(e); }
    },
    async loadHouseholds() {
      try {
        const r = await executeJson({ function: 'getCommunityHouseholds', chp_id: this.hhFilterChp, search: this.hhSearch }, hms);
        if (r.data && r.data.success) this.households = (r.data.data && r.data.data.Households) || [];
        if (!this.chps.length) await this.loadChps();
      } catch (e) { console.error(e); }
    },

    filterChpsByChu(cu) { this.chpFilterChu = cu.chu_id; this.tab = 'chps'; this.loadChps(); },
    filterHouseholdsByChp(c) { this.hhFilterChp = c.chp_id; this.tab = 'households'; this.loadHouseholds(); },

    onChuChange() {
      // reset CHP if it doesn't match new CHU
      if (this.hh.chp_id) {
        const c = this.chps.find(x => x.chp_id == this.hh.chp_id);
        if (!c || c.chu_id != this.hh.chu_id) this.hh.chp_id = null;
      }
      const cu = this.units.find(u => u.chu_id == this.hh.chu_id);
      if (cu) this.hh.branch_id = cu.branch_id;
    },

    openChuModal(cu) { this.chu = cu ? { ...this.blankChu(), ...cu } : this.blankChu(); this.chuModal = true; },
    async saveChu() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveCommunityUnit', ...this.chu }, hms);
        if (r.data && r.data.success) { this.chuModal = false; await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openChpModal(c) {
      this.chp = c ? { ...this.blankChp(), ...c } : this.blankChp();
      if (!this.chp.branch_id && this.chp.chu_id) {
        const cu = this.units.find(u => u.chu_id == this.chp.chu_id);
        if (cu) this.chp.branch_id = cu.branch_id;
      }
      this.chpModal = true;
    },
    async saveChp() {
      // Auto-set branch from CHU
      const cu = this.units.find(u => u.chu_id == this.chp.chu_id);
      if (cu) this.chp.branch_id = cu.branch_id;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveCommunityChp', ...this.chp }, hms);
        if (r.data && r.data.success) { this.chpModal = false; await this.loadChps(); await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openHhModal(h) {
      this.hh = h ? { ...this.blankHh(), ...h } : this.blankHh();
      this.hhModal = true;
    },
    async saveHh() {
      const cu = this.units.find(u => u.chu_id == this.hh.chu_id);
      if (cu) this.hh.branch_id = cu.branch_id;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveCommunityHousehold', ...this.hh }, hms);
        if (r.data && r.data.success) { this.hhModal = false; await this.loadHouseholds(); await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openVisitModal(h) {
      this.visit = this.blankVisit();
      this.visit.household_id = h.household_id;
      this.visit.chp_id = h.chp_id;
      this.visit._hh_name = h.head_name;
      this.visitModal = true;
    },
    async saveVisit() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordCommunityVisit', ...this.visit }, hms);
        if (r.data && r.data.success) { this.visitModal = false; await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    formatNumber(n) { const x = parseInt(n || 0); return x.toLocaleString(); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 28px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-warning { color: #c05621; } .hms-stat-success { color: #276749; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 12px; }

.hms-chu-card { background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #4299e1; border-radius: 8px; padding: 14px; }
.hms-chu-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; margin-bottom: 8px; }
.hms-chu-meta { display: grid; grid-template-columns: repeat(2, 1fr); gap: 4px; font-size: 13px; margin-bottom: 6px; }
.hms-vk { display: inline-block; min-width: 80px; color: #a0aec0; font-size: 10px; text-transform: uppercase; }
.hms-name { font-weight: 600; color: #1a202c; font-size: 15px; }
.hms-chu-actions { display: flex; gap: 6px; margin-top: 10px; align-items: center; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-info  { background: #bee3f8; color: #2a4365; }
.hms-tag { padding: 2px 6px; border-radius: 6px; font-size: 10px; font-weight: 600; }
.hms-tag-info    { background: #bee3f8; color: #2a4365; }
.hms-tag-warn    { background: #feebc8; color: #7b341e; }
.hms-tag-success { background: #c6f6d5; color: #22543d; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }

.hms-muted-sm { color: #a0aec0; font-size: 11px; }
</style>
